import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectIsProfileLoaded, selectRole } from "@/redux/selectors/authSelectors";

import { checkPermissionByRole } from "../helpers/checkPermissionByRole";
import type { PermissionKey, PredicateContext } from "../types";

/**
 * React hook to filter items based on permission keys.
 * Items without a permissionKey are always included.
 * Items with a permissionKey are checked against the current user's permissions.
 *
 * DANGER — mixed-context lists: predicateContext is passed to every item's
 * permission check regardless of which key that item uses. If the list contains
 * items with different permissionKeys that require different context shapes,
 * the caller must supply the union of all required fields. A missing field
 * causes the allowIf predicate to silently return false — no type error, no
 * runtime error. For per-key type safety use useCheckPermission individually.
 */
export function useFilterPermittedItems<T extends { permissionKey?: PermissionKey }>({
  items,
  predicateContext,
}: {
  items: T[];
  predicateContext?: PredicateContext;
}): T[] {
  const role = useSelector(selectRole);
  const isProfileLoaded = useSelector(selectIsProfileLoaded);

  return useMemo(() => {
    return items.filter(({ permissionKey }) => {
      if (!permissionKey) return true;
      // predicateContext defined means the permission needs profile-sourced data — exclude
      // items until profile loads so allowIf predicates don't fire with undefined values.
      if (predicateContext !== undefined && !isProfileLoaded) return false;

      // Cast needed: items have heterogeneous permission keys so the per-key
      // generic enforcement of CheckPermissionByRoleProps cannot apply here.
      return checkPermissionByRole({ permissionKey, predicateContext, role } as any);
    });
  }, [items, predicateContext, isProfileLoaded, role]);
}
